import {
  useQueries,
  UseQueryOptions,
  QueryKey,
  UseQueryResult,
} from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

// 기존 QueryResponse 인터페이스 유지
interface QueryResponse<T> {
  data: T;
  status: number;
  message?: string;
}

// API 응답 타입을 유연하게 처리하기 위한 타입 가드
function isQueryResponse<T>(
  response: T | QueryResponse<T>
): response is QueryResponse<T> {
  return (
    typeof response === 'object' &&
    response !== null &&
    'data' in response &&
    'status' in response
  );
}

// 수정된 WckQueryOptions 타입
type WckQueryOptions<T> = Omit<
  UseQueryOptions<T | QueryResponse<T>, Error, T, QueryKey>,
  'queryKey' | 'queryFn'
> & {
  queryKey: QueryKey;
  queryFn: () => Promise<T | QueryResponse<T>>;
  initialDataEnabled?: boolean;
};

export function useWckQueries<T extends readonly unknown[]>(queries: {
  [K in keyof T]: WckQueryOptions<T[K]>;
}): {
  [K in keyof T]: UseQueryResult<T[K], Error>;
} {
  const [isMounted, setIsMounted] = useState(false);

  // 각 쿼리에 대해 infiniteQueryKey를 생성
  const infiniteQueryKeys = useMemo(
    () => queries.map(query => ['wckQueryKey', ...query.queryKey]),
    [queries]
  );

  useEffect(() => {
    if (isMounted) {
      const filteredQueryData = Object?.entries(
        (window?.history?.state?.filteredQueryData as Record<
          string,
          unknown
        >) || {}
      )
        .filter(item => !infiniteQueryKeys.some(key => key.join() === item[0]))
        .reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: value,
          }),
          {}
        );
      window.history.replaceState(
        {
          ...window.history.state,
          filteredQueryData,
        },
        '',
        undefined
      );
    }
    setIsMounted(true);
  }, [isMounted]); // infiniteQueryKeys 의존성 제거

  // 각 쿼리에 대해 initialData를 설정
  const queriesWithInitialData = useMemo(
    () =>
      queries.map((query, index) => {
        const infiniteQueryKey = infiniteQueryKeys[index];
        const queryData = (() => {
          if (
            query.initialDataEnabled !== false &&
            typeof window !== 'undefined' &&
            window?.history?.state?.filteredQueryData
          ) {
            const filteredQueryData =
              window?.history?.state?.filteredQueryData?.[
                infiniteQueryKey?.join()
              ];
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return filteredQueryData;
          }
          return query.initialData;
        })();

        return {
          ...query,
          queryKey: infiniteQueryKey,
          initialData: queryData,
        };
      }),
    [queries, infiniteQueryKeys]
  );

  const results = useQueries({
    queries: queriesWithInitialData,
  });

  return results as {
    [K in keyof T]: UseQueryResult<T[K], Error>;
  };
}
